import React, { useState } from "react";

import { FiGithub, FiLinkedin, FiGlobe, FiShare2, FiMail, FiPhone, FiFileText, FiLink, FiMoreVertical } from "react-icons/fi";

import toast from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";

import Modal from "../modal";
import Loader from "../loader";
import InformationTab from "./information-tab";
import QuestionsTab from "./questions-tab";
import ResumeTab from "./resume-tab";
import AIResumeTab from "./ai-resume-tab";
import AvailabilityTab from "./availability-tab";
import CommentsTab from "./comments-tab";
import ConversationTab from "./conversation-tab";
import RawDataTab from "./raw-data-tab";
import ExperiencesTab from "./experiences-tab";
import EducationTab from "./education-tab";
import AppAiScoring from "./app_ai_scoring";
import InterviewPrepTab from "./interview-prep-tab";

import api from "@/services/api";
import { downloadResume } from "@/utils";

const APP_STATUS_DOTS = {
  pending: "bg-amber-400",
  in_progress: "bg-indigo-400 animate-pulse",
  done: "bg-green-400",
  error: "bg-red-400",
};

// Simple Link Share Button Component
function SimpleLinkShareButton({ candidateId }) {
  const { t } = useComponentTranslation(translations);

  if (!candidateId) return null;

  const handleShare = async () => {
    try {
      const shareUrl = `${window.location.origin}/recruiter/pool?user_id=${candidateId}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("link_copied"));
    } catch (e) {
      toast.error(t("error_copying_link"));
    }
  };

  return (
    <button
      onClick={handleShare}
      className="px-3 py-1.5 text-sm font-medium text-primary border border-primary hover:bg-primary hover:text-white rounded-lg transition-colors flex items-center gap-2"
      title={t("share_link")}
    >
      <FiLink className="w-4 h-4" />
      <span className="hidden sm:inline">{t("share")}</span>
    </button>
  );
}

// Share Button Component
function ShareButton({ shareLink, onShare }) {
  const { t } = useComponentTranslation(translations);
  const [showUserSelector, setShowUserSelector] = useState(false);

  if (!shareLink) return null;

  const handleShare = async (selectedUsers) => {
    try {
      if (onShare) {
        await onShare(selectedUsers);
      }
      setShowUserSelector(false);
      toast.success(t("shared_successfully"));
    } catch (e) {
      toast.error(t("error_sharing"));
    }
  };

  return (
    <>
      <button
        onClick={() => setShowUserSelector(true)}
        className="px-3 py-1.5 text-sm font-medium text-primary border border-primary hover:bg-primary hover:text-white rounded-lg transition-colors flex items-center gap-2"
        title={t("share")}
      >
        <FiShare2 className="w-4 h-4" />
        <span className="hidden sm:inline">{t("share")}</span>
      </button>

      {showUserSelector && <UserSelectorModal isOpen={showUserSelector} onClose={() => setShowUserSelector(false)} onSelect={handleShare} shareLink={shareLink} />}
    </>
  );
}

// User Selector Modal Component
function UserSelectorModal({ isOpen, onClose, onSelect, shareLink }) {
  const { t } = useComponentTranslation(translations);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { ok, data } = await api.get("/user");
      if (ok) {
        setUsers(data);
      }
    } catch (e) {
      toast.error(t("error_fetching_users"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (selectedUsers.length > 0) {
      onSelect(selectedUsers);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-md">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("share_profile")}</h3>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : (
          <>
            <div className="mb-4 max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {users.map((user) => (
                <label key={user._id} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers([...selectedUsers, user._id]);
                      } else {
                        setSelectedUsers(selectedUsers.filter((id) => id !== user._id));
                      }
                    }}
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {user.firstname} {user.lastname}
                    </p>
                    {user.email && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
                  </div>
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors">
                {t("cancel")}
              </button>
              <button
                onClick={handleSubmit}
                disabled={selectedUsers.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("share")} ({selectedUsers.length})
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// Footer Actions Component
function FooterActions({ application, setApplication, onPrevious, onNext }) {
  const { t } = useComponentTranslation(translations);
  const [rejecting, setRejecting] = useState(false);

  if (!application) return null;

  const handleReject = async () => {
    if (rejecting) return;
    if (!window.confirm(t("confirm_reject"))) return;
    setRejecting(true);
    try {
      const { ok, data } = await api.put(`/application/${application._id}`, { status: "REFUSED" });
      if (!ok) throw new Error();
      toast.success(t("status_updated"));
      setApplication({ ...application, ...(data || {}), status: "REFUSED" });
      setApplication(null);
    } catch (e) {
      toast.error(t("error_updating_status"));
    } finally {
      setRejecting(false);
    }
  };

  const handleMoveToInterview = async () => {
    toast.success(t("coming_soon_interview"));
    // try {
    //   const { ok } = await api.put(`/application/${application._id}`, { status: "INTERVIEW" });
    //   if (!ok) throw new Error();
    //   setApplication({ ...application, status: "INTERVIEW" });
    //   toast.success(t("status_updated"));
    //   if (onNext) onNext();
    // } catch (e) {
    //   toast.error(t("error_updating_status"));
    // }
  };

  const handlePrevious = () => {
    if (onPrevious) {
      // toast.success(t("coming_soon_previous"));
      onPrevious();
    }
  };

  const handleNext = () => {
    if (onNext) {
      // toast.success(t("coming_soon_next"));
      onNext();
    }
  };

  return (
    <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center shrink-0">
      <div className="flex items-center gap-3">
        <button
          onClick={handlePrevious}
          disabled={!onPrevious}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <span>←</span>
          {t("previous")}
        </button>

        <button
          onClick={handleNext}
          disabled={!onNext}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {t("next")}
          <span>→</span>
        </button>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleReject}
          disabled={rejecting}
          className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {rejecting ? <span className="h-4 w-4 border-2 border-red-200 border-l-red-700 rounded-full animate-spin" /> : <span>✕</span>}
          {t("reject")}
        </button>

        <button disabled className="px-4 py-2 text-sm font-medium text-gray-400 bg-gray-100 rounded-lg flex items-center gap-2 cursor-not-allowed opacity-60">
          <span>✓</span>
          {t("move_to_interview")}
        </button>
      </div>
    </div>
  );
}

const translations = {
  en: {
    error_fetching_data: "Error fetching data",
    error_updating_status: "Error updating status",
    status_updated: "Status updated",
    tab_information: "Information",
    tab_screening: "Screening",
    tab_interview: "Interview prep",
    tab_questions: "Questions and Answers",
    tab_resume: "Resume",
    tab_ai_resume: "AI Resume",
    tab_availability: "Availability",
    tab_comments: "Comments",
    tab_conversation: "Conversation",
    tab_experiences: "Experiences",
    tab_education: "Education",
    age: "years old",
    disability: "Person with disability",
    refuse: "Refuse",
    preselect: "Preselect",
    interview: "Interview",
    recruit: "Recruit",
    reject: "Reject",
    on_hold: "On Hold",
    previous: "Previous",
    next: "Next",
    move_to_interview: "Move to Interview",
    share: "Share",
    share_link: "Share link",
    link_copied: "Link copied to clipboard!",
    error_copying_link: "Error copying link",
    share_profile: "Share Profile",
    shared_successfully: "Shared successfully",
    error_sharing: "Error sharing",
    error_fetching_users: "Error fetching users",
    cancel: "Cancel",
    confirm_reject: "Reject this candidate?",
    coming_soon_reject: "Coming soon: Reject",
    coming_soon_interview: "Coming soon: Move to Interview",
    coming_soon_previous: "Coming soon: Previous",
    coming_soon_next: "Coming soon: Next",
  },
  fr: {
    error_fetching_data: "Erreur lors de la récupération des données",
    error_updating_status: "Erreur lors de la mise à jour du statut",
    status_updated: "Statut mis à jour",
    tab_information: "Informations",
    tab_screening: "Screening",
    tab_interview: "Préparation entretien",
    tab_questions: "Questions et Réponses",
    tab_resume: "CV",
    tab_ai_resume: "CV IA",
    tab_availability: "Disponibilité",
    tab_comments: "Commentaires",
    tab_conversation: "Conversation",
    tab_experiences: "Expériences",
    tab_education: "Formation",
    age: "ans",
    disability: "Personne en situation de handicap",
    refuse: "Refuser",
    preselect: "Présélectionner",
    interview: "Entretien",
    recruit: "Recruter",
    reject: "Rejeter",
    on_hold: "En attente",
    previous: "Précédent",
    next: "Suivant",
    move_to_interview: "Passer en entretien",
    share: "Partager",
    share_link: "Partager le lien",
    link_copied: "Lien copié dans le presse-papier !",
    error_copying_link: "Erreur lors de la copie du lien",
    share_profile: "Partager le profil",
    shared_successfully: "Partagé avec succès",
    error_sharing: "Erreur lors du partage",
    error_fetching_users: "Erreur lors de la récupération des utilisateurs",
    cancel: "Annuler",
    confirm_reject: "Rejeter ce candidat ?",
    coming_soon_reject: "Bientôt disponible : Rejeter",
    coming_soon_interview: "Bientôt disponible : Passer en entretien",
    coming_soon_previous: "Bientôt disponible : Précédent",
    coming_soon_next: "Bientôt disponible : Suivant",
  },
};

const TABS = {
  INFORMATION: "information",
  SCREENING: "screening",
  INTERVIEW: "interview",
  QUESTIONS: "questions",
  RESUME: "resume",
  AI_RESUME: "ai_resume",
  AVAILABILITY: "availability",
  COMMENTS: "comments",
  CONVERSATION: "conversation",
  EXPERIENCES: "experiences",
  EDUCATION: "education",
  RAW_DATA: "raw_data",
};

// 3-dot dropdown for secondary tabs
function MoreTabsMenu({ activeTab, setActiveTab, t, candidate, application }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const OVERFLOW_TABS = [TABS.AI_RESUME, TABS.EXPERIENCES, TABS.EDUCATION, TABS.QUESTIONS, TABS.RAW_DATA];
  const isOverflowActive = OVERFLOW_TABS.includes(activeTab);

  const items = [
    { key: TABS.AI_RESUME, label: t("tab_ai_resume") },
    { key: TABS.EXPERIENCES, label: t("tab_experiences"), count: candidate?.experiences?.length },
    { key: TABS.EDUCATION, label: t("tab_education"), count: candidate?.education?.length },
    { key: TABS.QUESTIONS, label: t("tab_questions"), count: (application?.custom_answers?.length || 0) + (application?.job_application_ai_qna?.length || 0) },
    { key: TABS.RAW_DATA, label: "Raw Data" },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-1 px-3 py-3 text-sm font-medium transition-colors
          border-b-2 -mb-px
          ${isOverflowActive ? "text-primary border-primary" : "text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300"}
        `}
      >
        <FiMoreVertical className="w-5 h-5" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[200px] py-1">
          {items.map((item) => (
            <button
              key={item.key}
              onClick={() => {
                setActiveTab(item.key);
                setIsOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${
                activeTab === item.key ? "text-primary font-medium bg-primary/5" : "text-gray-700"
              }`}
            >
              <span>{item.label}</span>
              {item.count > 0 && <span className="px-1.5 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">{item.count}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CandidateModal({ application, setApplication, candidate, setCandidate, job, setJob, onPrevious, onNext, shareLink, onShare }) {
  const { t } = useComponentTranslation(translations);
  const [activeTab, setActiveTab] = useState(TABS.INFORMATION);

  if (!candidate) return <></>;

  return (
    <Modal
      isOpen={!!candidate}
      onClose={() => {
        setCandidate(null);
        if (setApplication) setApplication(null);
        setActiveTab(TABS.INFORMATION);
      }}
      className="w-full max-w-7xl flex flex-col max-h-[90vh]"
    >
      {/* Profile header */}
      <div className="px-6 pr-12 py-3 border-b border-gray-200 shrink-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Avatar and basic info */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {candidate.avatar && <img className="h-12 w-12 rounded-full" src={candidate.avatar} alt={`${candidate.firstname} ${candidate.lastname}`} />}
            {!candidate.avatar && (
              <div className="h-12 w-12 rounded-full bg-gray-400 font-bold flex items-center justify-center text-white text-lg">
                {candidate.firstname.charAt(0)}
                {candidate.lastname.charAt(0)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {candidate.firstname} {candidate.lastname}
                    </h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {candidate.email && (
                      <a href={`mailto:${candidate.email}`} className="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" title={candidate.email}>
                        <FiMail className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {candidate.phone && (
                      <a href={`tel:${candidate.phone}`} className="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" title={candidate.phone}>
                        <FiPhone className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {candidate.linkedin && (
                      <a
                        href={candidate.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                        title="LinkedIn"
                      >
                        <FiLinkedin className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {candidate.github && (
                      <a
                        href={candidate.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded-md bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors"
                        title="GitHub"
                      >
                        <FiGithub className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {candidate.portfolio && (
                      <a
                        href={candidate.portfolio}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded-md bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
                        title="Portfolio"
                      >
                        <FiGlobe className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {candidate.resume_url && (
                      <button
                        type="button"
                        onClick={() => downloadResume(candidate)}
                        className="p-1.5 rounded-md bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                        title="Resume"
                      >
                        <FiFileText className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 text-xs text-gray-600 mt-1.5 w-full justify-between">
                    <div className="flex flex-wrap items-center gap-x-2">
                      {candidate.licenses?.length > 0 && <span>{candidate.licenses.map((l) => l.type).join(", ")}</span>}
                      {candidate.licenses?.length > 0 && (candidate.location || candidate.gender) && <span>•</span>}
                      {candidate.location && <span>{candidate.location}</span>}
                      {candidate.location && candidate.gender && <span>•</span>}
                      {candidate.gender && <span>{candidate.gender}</span>}
                      {(candidate.location || candidate.gender) && candidate.birth_date && <span>•</span>}
                      {candidate.birth_date && (
                        <span>
                          {new Date().getFullYear() - new Date(candidate.birth_date).getFullYear()} {t("age")}
                        </span>
                      )}
                      {(candidate.location || candidate.gender || candidate.birth_date) && candidate.handicap_situation && <span>•</span>}
                      {candidate.handicap_situation && <span className="text-primary font-medium">{t("disability")}</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Share Button */}
          <SimpleLinkShareButton candidateId={candidate._id} />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="px-6 border-b border-gray-200">
        <nav className="flex gap-1" aria-label="Tabs">
          <button
            onClick={() => setActiveTab(TABS.INFORMATION)}
            className={`
              flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors
              border-b-2 -mb-px
              ${activeTab === TABS.INFORMATION ? "text-primary border-primary" : "text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300"}
            `}
          >
            <span>{t("tab_information")}</span>
          </button>
          <button
            onClick={() => setActiveTab(TABS.RESUME)}
            className={`
              flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors
              border-b-2 -mb-px
              ${activeTab === TABS.RESUME ? "text-primary border-primary" : "text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300"}
            `}
          >
            <span>{t("tab_resume")}</span>
          </button>

          <button
            onClick={() => setActiveTab(TABS.COMMENTS)}
            className={`
              flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors
              border-b-2 -mb-px
              ${activeTab === TABS.COMMENTS ? "text-primary border-primary" : "text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300"}
            `}
          >
            <span>{t("tab_comments")}</span>
            {candidate.notes?.length > 0 && (
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${activeTab === TABS.COMMENTS ? "bg-primary text-white" : "bg-gray-200 text-gray-700"}`}>
                {candidate.notes.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab(TABS.CONVERSATION)}
            className={`
              flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors
              border-b-2 -mb-px
              ${activeTab === TABS.CONVERSATION ? "text-primary border-primary" : "text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300"}
            `}
          >
            <span>{t("tab_conversation")}</span>
          </button>

          {/* AI Scoring tab — visible whenever a screening run exists */}
          {(application?.screening_status || application?.screening_score != null) && (
            <button
              onClick={() => setActiveTab(TABS.SCREENING)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors
                border-b-2 -mb-px
                ${activeTab === TABS.SCREENING ? "text-primary border-primary" : "text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300"}
              `}
            >
              <span>⚡ AI Scoring</span>
              <span className={`w-2 h-2 rounded-full ${APP_STATUS_DOTS[application.screening_status] || "bg-gray-300"}`} />
            </button>
          )}

          {application && (
            <button
              onClick={() => setActiveTab(TABS.INTERVIEW)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors
                border-b-2 -mb-px
                ${activeTab === TABS.INTERVIEW ? "text-primary border-primary" : "text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300"}
              `}
            >
              <span>{t("tab_interview")}</span>
            </button>
          )}

          {/* More menu — pushed to right */}
          <div className="ml-auto" />
          <MoreTabsMenu activeTab={activeTab} setActiveTab={setActiveTab} t={t} candidate={candidate} application={application} />
        </nav>
      </div>

      {/* Tab Content */}
      <div className="px-6 py-6 overflow-y-auto flex-1">
        {/* Information Tab */}
        {activeTab === TABS.INFORMATION && <InformationTab candidate={candidate} job={job} />}
        {activeTab === TABS.RESUME && <ResumeTab candidate={candidate} />}
        {activeTab === TABS.AI_RESUME && <AIResumeTab candidate={candidate} />}
        {activeTab === TABS.EXPERIENCES && <ExperiencesTab candidate={candidate} />}
        {activeTab === TABS.EDUCATION && <EducationTab candidate={candidate} />}
        {activeTab === TABS.QUESTIONS && <QuestionsTab application={application} candidate={candidate} />}
        {activeTab === TABS.COMMENTS && <CommentsTab candidate={candidate} setCandidate={setCandidate} />}
        {activeTab === TABS.CONVERSATION && <ConversationTab candidate={candidate} application={application} />}
        {activeTab === TABS.SCREENING && <AppAiScoring application={application} />}
        {activeTab === TABS.INTERVIEW && (
          <InterviewPrepTab application={application} setApplication={setApplication} job={job} setJob={setJob} candidate={candidate} />
        )}
        {activeTab === TABS.RAW_DATA && <RawDataTab candidate={candidate} application={application} />}
      </div>

      <FooterActions application={application} setApplication={setApplication} onPrevious={onPrevious} onNext={onNext} />
    </Modal>
  );
}
